import { AllHtmlEntities } from 'html-entities';
import { Completion, LookupTree } from '../types/types';

const entities = new AllHtmlEntities();

interface LookupItem {
  id: string;
  name: string;
  aliases: string[];
  ignoreCanonicalName?: boolean;
  caseSensitiveNames?: boolean;
  type: string;
}

export const makeLookupTreeFromItems = (items: LookupItem[]) => {
  const linkNamesAndIds = [
    ...items.filter(item => !item.ignoreCanonicalName).map(item => ({ id: item.id, name: item.name, canonicalName: item.name, type: item.type, caseSensitiveNames: item.caseSensitiveNames })),
    ...items.map(item => item.aliases.map(alias => ({ id: item.id, name: alias, canonicalName: item.name, type: item.type, caseSensitiveNames: item.caseSensitiveNames })))
   ].flat().sort((a, b) => b.name.length - a.name.length);

  const lookupTree: LookupTree = new Map();

  const getOrCreateSubtreeFromName = (name: string, caseInsensitive: boolean) => {
    let subTree = lookupTree;
    for (const c of entities.encode(name)) {
      const key = caseInsensitive ? '!' + c.toLowerCase() : c;
      const newSubTree: LookupTree = (subTree.get(key) as LookupTree || new Map());
      subTree.set(key, newSubTree);
      subTree = newSubTree;
    }
    return subTree;
  };

  for (const { name, id, canonicalName, type, caseSensitiveNames } of linkNamesAndIds) {
    const escapedName = name.replace(/([\\*`])/g, '\\$1');
    const literalSubTree = getOrCreateSubtreeFromName(escapedName, false);
    literalSubTree.set('id', id);
    literalSubTree.set('name', escapedName);
    literalSubTree.set('canonicalName', canonicalName);
    literalSubTree.set('type', type);
    if (!caseSensitiveNames) {
      const caseInsensitiveSubTree = getOrCreateSubtreeFromName(escapedName, true);
      caseInsensitiveSubTree.set('id', id);
      caseInsensitiveSubTree.set('name', escapedName);
      caseInsensitiveSubTree.set('canonicalName', canonicalName);
      caseInsensitiveSubTree.set('type', type);
    }
  }

  return lookupTree;
};

interface Traverser {
  pos: number;
  subTree: LookupTree;
  caseInsensitive: boolean;
}

interface Match {
  encodedStart: number;
  encodedEnd: number;
  id: string;
  nameLength: number;
  type: string;
}

export interface ImplicitLinkMatchPosition {
  unencodedStart: number;
  unencodedEnd: number;
  encodedStart: number;
  encodedEnd: number;
  id: string;
  type: string;
}

// Note, because it has a capturing group, splitting using this
// regex will include the explicit links in the resulting array
// every alternate element.
const EXPLICIT_LINK_REGEX = /((?<!\\)\[[^\]]*?\]\([^)]*?\))/;

export const resolveAllImplicitLinks = (lookupTree: LookupTree, unencodedText: string, ignoredItemId?: string) => {
  const foundIds = new Set<string>();
  const result: { id: string, name: string }[] = [];
  const textParts = unencodedText.split(EXPLICIT_LINK_REGEX);
  textParts.forEach((unencodedPart, index) => {
    if (index % 2 === 0) {
      const found = resolveAllImplicitLinksInternal(lookupTree, unencodedPart, ignoredItemId);
      for (let link of found) {
        if (!foundIds.has(link.id)) {
          foundIds.add(link.id);
          result.push(link);
        }
      }
    }
  });
  return result;
};

export const produceEncodedTextWithImplicitLinks = (lookupTree: LookupTree, unencodedText: string, ignoredItemId?: string) => {
  const textParts = unencodedText.split(EXPLICIT_LINK_REGEX);
  const results = textParts.map((unencodedPart, index) => {
    if (index % 2 === 0) {
      const { encodedText, matches } = resolveNonOverlappingImplicitLinkSpansInternal(lookupTree, unencodedPart, ignoredItemId);
      let output = "";
      let cursor = 0;
      for (const match of matches) {
        output += encodedText.slice(cursor, match.encodedStart);
        output += `<a href='#' data-id='${match.id}' class='${match.type}'>${encodedText.slice(match.encodedStart, match.encodedEnd + 1)}</a>`;
        cursor = match.encodedEnd + 1;
      }
      output += encodedText.slice(cursor);
      return output;
    }
    return unencodedPart;
  });
  return results.join('');
}

export const resolveNonOverlappingImplicitLinkSpans = (lookupTree: LookupTree, unencodedText: string, ignoredItemId?: string) => {
  const textParts = unencodedText.split(EXPLICIT_LINK_REGEX);
  let unencodedOffset = 0;
  let encodedOffset = 0;
  const allMatches: ImplicitLinkMatchPosition[] = [];
  textParts.forEach((unencodedPart, index) => {
    let encodedPartLength = entities.encode(unencodedPart).length;
    if (index % 2 === 0) {
      const { encodedText, matches } = resolveNonOverlappingImplicitLinkSpansInternal(lookupTree, unencodedPart, ignoredItemId);
      encodedPartLength = encodedText.length;
      allMatches.push(...matches.map(match => ({
        ...match,
        unencodedStart: match.unencodedStart + unencodedOffset,
        unencodedEnd: match.unencodedEnd + unencodedOffset,
        encodedStart: match.encodedStart + encodedOffset,
        encodedEnd: match.encodedEnd + encodedOffset
      })));
    }
    unencodedOffset += unencodedPart.length;
    encodedOffset += encodedPartLength;
  });
  return allMatches;
}

const resolveNonOverlappingImplicitLinkSpansInternal = (lookupTree: LookupTree, unencodedText: string, ignoredItemId?: string) => {
  const encodedIndexToOriginalIndex: number[] = [];
  let encodedText = "";
  for (let i = 0; i < unencodedText.length; i++) {
    const encoded = entities.encode(unencodedText[i]);
    encodedText += encoded;
    for (let j = 0; j < encoded.length; j++) {
      encodedIndexToOriginalIndex.push(i);
    }
  }
  
  const implicitLinkMatches: ImplicitLinkMatchPosition[] = [];
  let stashedMatches: Match[] = [];
  let latestStashedMatchEnd = -1;

  const chooseAndOrderLongestNonOverlappingStashedMatches = () => {
    const chosenNonOverlappingMatches: Match[] = [];
    if (stashedMatches.length > 1) {
      stashedMatches.sort((a, b) => a.nameLength - b.nameLength);
      // Select the longest stashed match
      while (stashedMatches.length > 0) {
        const chosenMatch = stashedMatches.pop()!;
        // Remove all matches that overlap with the selected match
        stashedMatches = stashedMatches.filter(m => m.encodedEnd < chosenMatch.encodedStart || m.encodedStart > chosenMatch.encodedEnd);
        chosenNonOverlappingMatches.push(chosenMatch)
      }
    } else if (stashedMatches.length === 1) {
      chosenNonOverlappingMatches.push(stashedMatches[0]);
    }
    if (chosenNonOverlappingMatches.length > 0) {
      chosenNonOverlappingMatches.sort((a, b) => a.encodedStart - b.encodedStart);
    }
    for (const chosenMatch of chosenNonOverlappingMatches) {
      implicitLinkMatches.push({
        unencodedStart: encodedIndexToOriginalIndex[chosenMatch.encodedStart],
        unencodedEnd: encodedIndexToOriginalIndex[chosenMatch.encodedEnd],
        encodedStart: chosenMatch.encodedStart,
        encodedEnd: chosenMatch.encodedEnd,
        id: chosenMatch.id,
        type: chosenMatch.type
      });
    }
    return chosenNonOverlappingMatches;
  };

  traverseAndResolveLinks({
    onBeginNextCharacter: () => {},
    onLinkFound: (id, name, canonicalName, type, encodedStartPos, encodedEndPos) => {
      stashedMatches.push({ encodedStart: encodedStartPos, encodedEnd: encodedEndPos, id, nameLength: name.length, type });
      latestStashedMatchEnd = Math.max(latestStashedMatchEnd, encodedEndPos);
    },
    onEndNextCharacter: (char, pos, earliestActiveTraverserStartPos) => {
      if (stashedMatches.length > 0) {
        // If all stashed matches are now behind all ongoing traversers,
        // then we're in the clear to deal with the stash.
        if (latestStashedMatchEnd < earliestActiveTraverserStartPos) {
          chooseAndOrderLongestNonOverlappingStashedMatches();
          
          stashedMatches = [];
          latestStashedMatchEnd = -1;
        }
      }
    }
  }, lookupTree, encodedText, ignoredItemId);

  chooseAndOrderLongestNonOverlappingStashedMatches();

  return {
    encodedText,
    matches: implicitLinkMatches,
  };
};

const resolveAllImplicitLinksInternal = (lookupTree: LookupTree, unencodedText: string, ignoredItemId?: string) => {
  let encodedText = entities.encode(unencodedText);
  const foundLinks: { id: string, name: string }[] = [];

  traverseAndResolveLinks({
    onBeginNextCharacter: () => {},
    onLinkFound: (id, name, canonicalName) => {
      foundLinks.push({ id, name: canonicalName });
    },
    onEndNextCharacter: () => {}
  }, lookupTree, encodedText, ignoredItemId);

  return foundLinks;
};

const traverseAndResolveLinks = (
  callbacks: {
    onBeginNextCharacter: (char: string, pos: number) => void,
    onLinkFound: (id: string, name: string, canonicalName: string, type: string, encodedStartPos: number, encodedEndPos: number) => void,
    onEndNextCharacter: (char: string, pos: number, earliestActiveTraverserStartPos: number) => void
  },
  lookupTree: LookupTree,
  encodedText: string,
  ignoredItemId?: string) => {
  // Note that one traverser does NOT equal one match. One traverser equals one starting position. It can find multiple matches.
  let traversers: (Traverser|null)[] = [];
  let currentPos = -1;
  for (const c of encodedText) {
    currentPos += 1;
    traversers.push({ pos: currentPos, subTree: lookupTree, caseInsensitive: false });
    traversers.push({ pos: currentPos, subTree: lookupTree, caseInsensitive: true });
    callbacks.onBeginNextCharacter(c, currentPos);
    let earliestActiveTraverserStartPos = currentPos + 1;
    for (let t = 0; t < traversers.length; t++) {
      const traverser = traversers[t];
      if (traverser === null) {
        continue; // Not needed, but keeps type checker happy
      }
      const key = traverser.caseInsensitive ? '!' + c.toLowerCase() : c;
      const nextLevel = traverser.subTree.get(key) as LookupTree|undefined;
      if (nextLevel === undefined) {
        traversers[t] = null;
        continue;
      }
      const testId = nextLevel.get('id') as string;
      if (testId) {
        if (testId !== ignoredItemId) {
          callbacks.onLinkFound(testId, nextLevel.get('name') as string, nextLevel.get('canonicalName') as string, nextLevel.get('type') as string, traverser.pos, currentPos);
        }
        // NOTE! MUST CONTINUE TRAVERSER
        // Because there may be longer matches from here
      }

      traverser.subTree = nextLevel;
      if (traverser.pos < earliestActiveTraverserStartPos) {
        earliestActiveTraverserStartPos = traverser.pos;
      }
    }

    traversers = traversers.filter(t => t !== null);
    callbacks.onEndNextCharacter(c, currentPos, earliestActiveTraverserStartPos);
  }
};

export const textHasMatch = (lookupTree: LookupTree, text: string) => {
  const encodedText = entities.encode(text);
  
  // Note that one traverser does NOT equal one match. One traverser equals one starting position. It can find multiple matches.
  let traversers: (Traverser|null)[] = [];
  let currentPos = -1;
  for (const c of encodedText) {
    currentPos += 1;
    traversers.push({ pos: currentPos, subTree: lookupTree, caseInsensitive: false });
    traversers.push({ pos: currentPos, subTree: lookupTree, caseInsensitive: true });
    for (let t = 0; t < traversers.length; t++) {
      const traverser = traversers[t];
      if (traverser === null) {
        continue; // Not needed, but keeps type checker happy
      }
      const key = traverser.caseInsensitive ? '!' + c.toLowerCase() : c;
      const nextLevel = traverser.subTree.get(key) as LookupTree|undefined;
      if (nextLevel === undefined) {
        traversers[t] = null;
        continue;
      }
      const testId = nextLevel.get('id');
      if (testId) {
        return true;
      }
      traverser.subTree = nextLevel;
    }
    traversers = traversers.filter(t => t !== null);
  }

  return false;
};

export const findCompletions = (lookupTree: LookupTree, text: string) : Completion[] => {
  const prefix = text.substring(Math.max(0, text.length - 40));
  const encodedText = entities.encode(prefix);
  
  // Note that one traverser does NOT equal one match. One traverser equals one starting position. It can find multiple matches.
  let traversers: (Traverser|null)[] = [];
  let currentPos = -1;
  let allowNewTraverser = true;
  for (const c of encodedText) {
    currentPos += 1;
    const isLetter = c.toLowerCase() !== c.toUpperCase();
    if (isLetter && allowNewTraverser) {
      traversers.push({ pos: currentPos, subTree: lookupTree, caseInsensitive: false });
      traversers.push({ pos: currentPos, subTree: lookupTree, caseInsensitive: true });
      allowNewTraverser = false;
    }
    if (!isLetter) {
      allowNewTraverser = true;
    }
    
    for (let t = 0; t < traversers.length; t++) {
      const traverser = traversers[t];
      if (traverser === null) {
        continue; // Not needed, but keeps type checker happy
      }
      const key = traverser.caseInsensitive ? '!' + c.toLowerCase() : c;
      const nextLevel = traverser.subTree.get(key) as LookupTree|undefined;
      if (nextLevel === undefined) {
        traversers[t] = null;
        continue;
      }

      traverser.subTree = nextLevel;
    }
    traversers = traversers.filter(t => t !== null);
  }

  traversers = traversers.filter(t => {
    const caseSensitiveMatch = !(t?.caseInsensitive);
    const matchLength = currentPos - t!.pos + 1;
    if (caseSensitiveMatch) {
      // Two letters required for case-sensitive match
      return matchLength >= 2;
    }
    // Five letters required for case-insensitive match
    return matchLength >= 5;
  });

  const foundIds = new Set<string>();

  const findPossibleCompletions = (tree: LookupTree, characterCount: number) : Completion[] => {
    const completions: Completion[] = [];
    const nextChars = Array.from(tree.keys()).filter(k => k !== 'id' && k !== 'name' && k !== 'canonicalName' && k !== 'type' && k !== 'special');
    const foundId = tree.get('id') as string|undefined;
    const isMatch = foundId !== undefined;
    if (isMatch && !foundIds.has(foundId)) {
      const name = tree.get('name') as string;
      const encodedName = entities.encode(name);
      completions.push({
        name: name,
        completion: entities.decode(encodedName.substring(encodedName.length - characterCount))
      });

      foundIds.add(foundId);
    }

    for (const nextChar of nextChars) {
      const nextTree = tree.get(nextChar) as LookupTree;
      const nextCompletions = findPossibleCompletions(nextTree, characterCount + 1);
      completions.push(...nextCompletions);
    }

    return completions;
  };

  return traversers.map(t => {
    let tree = t!.subTree;
    return findPossibleCompletions(tree, 0);
  }).flat();
};

export const lookupExactMatch = (lookupTree: LookupTree, text: string) => {
  const encodedText = entities.encode(text);
  
  let subTree = lookupTree;
  for (const c of encodedText) {
    const nextLevel = subTree.get(c) as LookupTree|undefined;
    if (nextLevel === undefined) {
      return undefined;
    }
    subTree = nextLevel;
  }

  const id = subTree.get('id') as string|undefined;
  if (id) {
    return { id, name: subTree.get('canonicalName') as string };
  }
  return undefined;
};